/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useMemo, useState } from 'react';
import { Form, Spinner, Badge } from 'react-bootstrap';
import BookingServicesService from '../../../Services/BookingServicesService';

/**
 * Attach optional booking add-on services (flowers, champagne, welcome
 * drinks, etc.) to a booking. Fetches the active catalog once on mount;
 * the picker is read-only against the catalog (admins create/edit
 * services on the Booking Extras admin page).
 *
 * Send shape is intentionally minimal — `{ service_id, quantity? }` —
 * because the server re-resolves unit price + currency from the
 * catalog at save time. Pre-existing bookings come in carrying full
 * snapshots; we strip them to the same minimal shape on output so the
 * server's tamper-proofing path takes effect either way.
 */
function BookingServicesPicker({
  selectedServices = [],
  onChange,
  passengerCount,
  bookingCurrency = 'EUR',
}) {
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await BookingServicesService.fetchBookingServices();
        if (!cancelled) {
          setCatalog(response?.data?.data || []);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.message || 'Failed to load booking services');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Index the current selections by service_id so checkbox state + quantity
  // come straight off the order. Tolerates both shapes the order might
  // arrive in: full snapshots (existing booking) or thin selections
  // (just edited the form).
  const selectionById = useMemo(() => {
    const map = {};
    (selectedServices || []).forEach((s) => {
      const id = s?.service_id;
      if (id) map[id] = s;
    });
    return map;
  }, [selectedServices]);

  const formatPrice = (svc) => {
    if (bookingCurrency === 'HUF') {
      return svc.price_huf != null ? `${Math.round(svc.price_huf).toLocaleString('en-US')} Ft` : '—';
    }
    return svc.price_eur != null ? `€${Number(svc.price_eur).toFixed(2)}` : '—';
  };

  // Default quantity hint shown to the admin before they touch the
  // stepper. Matches what the server defaults to in resolveBookingServices.
  const defaultQuantityFor = (svc) => {
    if (svc.price_type === 'per_person') return Math.max(1, Number(passengerCount) || 1);
    return 1;
  };

  const emit = (next) => {
    // Compact to `[{ service_id, quantity }]` only — strip snapshot
    // fields so the server's catalog-resolution path always runs.
    const compacted = next.map((s) => ({
      service_id: s.service_id,
      ...(s.quantity != null ? { quantity: s.quantity } : {}),
    }));
    onChange(compacted);
  };

  const toggleService = (svc, checked) => {
    const current = [...(selectedServices || [])];
    if (checked) {
      // Default quantity surfaces at the moment of selection so
      // the form posts a sane number even if admin doesn't touch
      // the stepper.
      current.push({ service_id: svc.Id, quantity: defaultQuantityFor(svc) });
    } else {
      const idx = current.findIndex((s) => s.service_id === svc.Id);
      if (idx >= 0) current.splice(idx, 1);
    }
    emit(current);
  };

  const setQuantity = (svcId, qty) => {
    const current = (selectedServices || []).map((s) => (
      s.service_id === svcId ? { ...s, quantity: qty } : s
    ));
    emit(current);
  };

  if (loading) {
    return (
      <div className="text-muted">
        <Spinner size="sm" animation="border" className="me-2" />
        Loading available services...
      </div>
    );
  }

  if (error) {
    return <div className="text-danger small">{error}</div>;
  }

  if (catalog.length === 0) {
    return (
      <Form.Text className="text-muted">
        No services in the catalog yet. Create some on the Booking Extras admin page.
      </Form.Text>
    );
  }

  return (
    <div>
      <Form.Text className="text-muted d-block mb-2">
        Pick optional extras. Per-person services default to the passenger count;
        flat services default to 1. Server re-derives prices from the catalog
        on save (admin edits to the catalog don&apos;t retro-affect existing bookings).
      </Form.Text>
      {catalog.map((svc) => {
        const selection = selectionById[svc.Id];
        const isSelected = !!selection;
        const qty = selection?.quantity ?? defaultQuantityFor(svc);
        return (
          <div key={svc.Id} className="d-flex align-items-center mb-2 p-2 border rounded">
            <Form.Check
              type="checkbox"
              id={`booking-service-${svc.Id}`}
              checked={isSelected}
              onChange={(e) => toggleService(svc, e.target.checked)}
              className="me-2"
            />
            <div className="flex-grow-1">
              <div>
                <strong>{svc.name}</strong>
                <Badge bg="light" text="dark" className="ms-2">
                  {svc.price_type === 'per_person' ? 'per person' : 'flat'}
                </Badge>
                {svc.category && (
                  <Badge bg="secondary" className="ms-1">{svc.category}</Badge>
                )}
              </div>
              {svc.description && (
                <small className="text-muted">{svc.description}</small>
              )}
            </div>
            <div className="text-end me-2" style={{ minWidth: 90 }}>
              {formatPrice(svc)}
            </div>
            <Form.Control
              type="number"
              size="sm"
              min={1}
              value={qty}
              disabled={!isSelected}
              onChange={(e) => setQuantity(svc.Id, Number(e.target.value) || 1)}
              style={{ width: 80 }}
              title="Quantity"
            />
          </div>
        );
      })}
    </div>
  );
}

export default BookingServicesPicker;
